import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Anchor } from "..";
import { paginationDataAction } from "../../redux/actions/customTableActions";
import {
  ASCENDING_SORT,
  DESCENDING_SORT,
  DUMMY_COMPANY_IMAGE,
  NO_RECORD_UPDATE,
  NO_SORT,
  PIN_ICON,
} from "../../utils/constants/assets";
import {
  BORDER,
  GREY,
  INTER_DIMENSIONAL_BLUE,
  PRIMARY,
  SUCCESS,
  WARNING,
} from "../../utils/constants/colors";
import { fontSize, fontWeight, ICON_LABELS } from "../../utils/constants/UI";
import {
  capitalizeFirstLetter,
  createCompanyURL,
  formatCurrency,
  textEllipsesHandler,
  timeStampToDate,
  timeStampToDateTime,
  timeStampToTime,
} from "../../utils/helpers";
import AvatarName from "../Avatar/AvatarName";
import AvatarOutlined from "../Avatar/AvatarOutlined";
import Checkbox from "../Buttons/Checkbox/Checkbox";
import IconComponent from "../Icon/IconComponent";
import Image from "../Image/Image";
import SkeletonLoader from "../Loaders/SkeletonLoader";
import TypographyInline from "../Typography/TypographyInline";
import CustomPagination from "./CustomPagination";
import { STRINGS } from "./strings";
import { styles } from "./style";
import { Button } from "../Buttons";
import StatusChip from "../StatusChip/StatusChip";
import CustomMenu from "../Menu/CustomMenu";
import FilterChip from "../FilterChip/FilterChip";
import CustomTooltip from "../Tooltip/CustomTooltip";
import classes from "./Table.module.css";
import AvatarNameWithLink from "./AvatarNameWithLink";
// import { useScrollDetection } from "../../../App";

const filterTableOperations = (operations, current_row, current_head) => {
  try {
    // filter operations based on folder type.
    operations = operations.filter(
      (item) =>
        (item.id === "clone" && current_row?.type !== "folder") ||
        item.id !== "clone"
    );
    if (Object.keys(current_row) && Object.keys(current_head)) {
      // if state is interrupted, remove resume option.
      if (current_row?.state === "interrupted") {
        const new_operations = operations.filter(
          (item) => !(item?.id?.toLowerCase() === "resume")
        );
        // check for pin and unpin label.
        if (Object.keys(current_row).includes("pin")) {
          return new_operations.map((item) => {
            return {
              ...item,
              label:
                item?.label === "Pin"
                  ? parseInt(current_row?.pin)
                    ? "Unpin"
                    : "Pin"
                  : item?.label,
            };
          });
        }
        return new_operations;
      }

      // if state is paused, remove pause option.
      else if (current_row?.state === "paused") {
        const new_operations = operations.filter(
          (item) => !(item?.id?.toLowerCase() === "pause")
        );
        // check for pin and unpin label.
        if (Object.keys(current_row).includes("pin")) {
          return new_operations.map((item) => {
            return {
              ...item,
              label:
                item?.label === "Pin"
                  ? parseInt(current_row?.pin)
                    ? "Unpin"
                    : "Pin"
                  : item?.label,
            };
          });
        }
        return new_operations;
      }

      // if state is active/ scheduled, remove resume option.
      else {
        const new_operations = operations.filter(
          (item) => !(item?.id?.toLowerCase() === "resume")
        );
        // check for pin and unpin label.
        if (Object.keys(current_row).includes("pin")) {
          return new_operations.map((item) => {
            return {
              ...item,
              label:
                item?.label === "Pin"
                  ? parseInt(current_row?.pin)
                    ? "Unpin"
                    : "Pin"
                  : item.label,
            };
          });
        }

        return new_operations;
      }
    }

    return operations;
  } catch (error) {
    console.log("error");
    return operations;
  }
};

const CustomTable = ({ tableData = {}, headerStrip = [] }) => {
  const dispatch = useDispatch();
  const emptyBodyHeads = [
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
  ];
  const tableHeads = tableData?.heads || [];
  const tableBody = tableData?.isLoading
    ? emptyBodyHeads
    : tableData?.body || [];
  const tableActions = tableData?.actions || [];
  const tableStausList = tableData?.status_list || [];
  const tableHeaderStripValue = tableData?.header_strip_selected_value;
  const table_id = tableData?.id || "";
  const disableKey = tableData?.disable_key;
  const selectionPermission = tableData?.is_selection_not_allowed;
  const paginationDataSelector = useSelector(
    (state) => state.getPaginationQuery
  );
  const checkedRowSelect = table_id
    ? paginationDataSelector?.data?.[table_id]?.checkedRow
    : paginationDataSelector?.data?.checkedRow;

  const [checkedValue, setCheckedValue] = useState(
    tableData?.id
      ? paginationDataSelector?.data?.[tableData?.id]?.checkedRow || []
      : paginationDataSelector?.data?.checkedRow || []
  );
  const [isCopied, setIsCopied] = useState("");
  const [isActionMenuVisible, setIsActionMenuVisible] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [leadStatusMenuAnchor, setLeadStatusMenuAnchor] = useState(null);
  const [clickedRowData, setClickedRowData] = useState({});
  const [clickedHeadData, setClickedHeadData] = useState({});
  const [clickedRowIndex, setClickedRowIndex] = useState();
  const [clickedHeadIndex, setClickedHeadIndex] = useState();

  const tableOperations = filterTableOperations(
    tableData?.operations || [],
    clickedRowData,
    clickedHeadData
  );

  // Storing id in redux for single checkbox.
  const checkedHandler = (e) => {
    let index = checkedValue.indexOf(e.target.value);
    let checkedArr = [...checkedValue];
    if (index === -1) {
      setCheckedValue([...checkedValue, e.target.value]);
      checkedArr.push(e.target.value);
      let nestedTable = {
        ...paginationDataSelector?.data,
        checkedRow: [],
      };
      if (table_id) {
        nestedTable[table_id] = {
          ...paginationDataSelector?.data?.[table_id],
          checkedRow: checkedArr,
        };
      } else {
        nestedTable = {
          ...paginationDataSelector?.data,
          checkedRow: checkedArr,
        };
      }
      dispatch(
        paginationDataAction({
          ...nestedTable,
        })
      );
    } else {
      checkedValue.splice(index, 1);
      setCheckedValue([...checkedValue]);
      checkedArr.splice(index, 1);
      let nestedTable = {
        ...paginationDataSelector?.data,
        checkedRow: checkedArr,
      };
      if (table_id) {
        nestedTable[table_id] = {
          ...paginationDataSelector?.data?.[table_id],
          checkedRow: checkedArr,
        };
      }
      dispatch(
        paginationDataAction({
          ...nestedTable,
        })
      );
    }
  };

  // Storing all ids in redux for all checkbox.
  const checkedAllHandler = () => {
    const all_operations_ids = disableKey?.length
      ? tableBody
          .filter((item) => {
            if (item[disableKey]) {
              return item._id;
            }
          })
          ?.map((item) => {
            return item?._id;
          })
      : tableBody.map((item) => item._id);
    const all_ids = [...all_operations_ids, ...checkedValue];
    const all_unique = [...new Set(all_ids)];
    if (checkedValue.length !== tableBody.length) {
      if (all_operations_ids?.length === checkedValue?.length) {
        let nestedTable = {
          ...paginationDataSelector?.data,
          checkedRow: [],
        };
        if (table_id) {
          nestedTable[table_id] = {
            ...paginationDataSelector?.data?.[table_id],
            checkedRow: [],
          };
        }
        dispatch(
          paginationDataAction({
            ...nestedTable,
          })
        );
        setCheckedValue([]);
      } else {
        let nestedTable = {
          ...paginationDataSelector?.data,
          checkedRow: [],
        };
        if (table_id) {
          nestedTable[table_id] = {
            ...paginationDataSelector?.data?.[table_id],
            checkedRow: all_unique,
          };
        } else {
          nestedTable = {
            ...paginationDataSelector?.data,
            checkedRow: all_unique,
          };
        }
        dispatch(
          paginationDataAction({
            ...nestedTable,
          })
        );
        setCheckedValue(all_unique);
      }
    } else {
      let nestedTable = {
        ...paginationDataSelector?.data,
        checkedRow: [],
      };
      if (table_id) {
        nestedTable[table_id] = {
          ...paginationDataSelector?.data?.[table_id],
          checkedRow: [],
        };
      }
      dispatch(
        paginationDataAction({
          ...nestedTable,
        })
      );
      setCheckedValue([]);
    }
  };

  // Copy Text Function.
  const copyTextToClipboard = (row, head, headIndex, rowIndex, copyText) => {
    navigator?.clipboard?.writeText(copyText?.length ? copyText : row[head.id]);
    setIsCopied(copyText?.length ? copyText : row[head.id]);
    setClickedRowIndex(rowIndex);
    setClickedHeadIndex(headIndex);
    setTimeout(() => {
      setIsCopied("");
    }, 2000);
  };

  // Storing id and sort direction to redux.
  const sortHandler = (head, index) => {
    let nestedTable = {
      ...paginationDataSelector.data,
      checkedRow: [],
      query: {
        ...paginationDataSelector.data?.query,
        sort: {
          id: head.id,
          direction:
            paginationDataSelector.data?.query?.sort?.direction === "asc"
              ? "desc"
              : "asc",
        },
      },
    };
    if (table_id) {
      nestedTable[table_id] = {
        ...paginationDataSelector.data?.[table_id],
        checkedRow: [],
        query: {
          ...paginationDataSelector.data?.[table_id]?.query,
          sort: {
            id: head.id,
            direction:
              paginationDataSelector.data?.[table_id]?.query?.sort
                ?.direction === "asc"
                ? "desc"
                : "asc",
          },
        },
      };
    }
    dispatch(
      paginationDataAction({
        ...nestedTable,
      })
    );
  };

  // Returns duration label as time in booked meeting timecell.
  const getBookedDurationLabel = (duration) => {
    if (parseInt(duration) === 60) {
      return "1 hr";
    } else if (parseInt(duration) === 90) {
      return "1hrs 30min";
    } else if (parseInt(duration) === 120) {
      return "2hrs";
    } else {
      return `${parseInt(duration)} min`;
    }
  };

  // Returns duration label as multiple or time in meeting timecell.
  const getDurationLabel = (row) => {
    let durationLength = row?.duration_options?.length;
    if (durationLength) {
      if (row?.duration_options.length > 1) {
        return STRINGS.MULTIPLE;
      } else {
        return row?.duration_options[0]?.label;
      }
    } else {
      return `${
        row?.duration_options
          ? `${row?.duration_options} min`
          : getBookedDurationLabel(row?.booked_duration)
      } `;
    }
  };

  // Action Icon Function opens menu having all operations.
  const actionIconHandler = (event, row, head) => {
    setIsActionMenuVisible(true);
    setActionMenuAnchor(event.currentTarget);
    setClickedRowData(row);
    setClickedHeadData(head);
  };

  // Action Icon Function opens menu having all operations.
  const leadStatusHandler = (event, row, head) => {
    setLeadStatusMenuAnchor(event.currentTarget);
    setClickedRowData(row);
    setClickedHeadData(head);
  };

  // Used to get task status.
  const getTaskStatusLabel = (row) => {
    if (row?.status && row?.is_lead) {
      return "Marked as Completed | Marked as Lead";
    } else if (row?.is_lead) {
      return "Marked as Lead";
    } else if (row?.status) {
      return "Marked as Completed";
    }
  };

  // Get time stamp according to id.
  const getTimeStampLabel = (row, head) => {
    if (head?.label === "Date" || head?.isDateLabel) {
      return timeStampToDate(
        row?.[head?.id] || row?.event_startDateTime || row?.lead_date_time
      );
    } else if (head?.label === "Time" || head?.isTimeLabel) {
      return timeStampToTime(row?.event_startDateTime || row?.lead_date_time);
    } else {
      if (row?.[head?.id] !== row?.event_startDateTime || row?.lead_date_time) {
        return timeStampToDateTime(row?.[head?.id]) || "--";
      }
      return timeStampToDateTime(
        row?.[head?.id] || row?.event_startDateTime || row?.lead_date_time
      );
    }
  };

  // Table Designs
  const textDesign = (row, head, headIndex, rowIndex) => {
    return (
      <CustomTooltip
        theme={tableActions[head.tooltip] ? "light" : "dark"}
        key={headIndex}
        title={
          !tableData?.isLoading &&
          (tableActions[head.tooltip]
            ? tableActions[head.tooltip]?.function(row, head)
            : window.innerWidth < 1250 || row[head.id]?.length > 20
            ? head?.isCapitalize
              ? capitalizeFirstLetter(row[head.id] || "")
              : Array.isArray(row[head.id])
              ? row[head.id]?.join()
              : row[head.id]
            : "")
        }
      >
        <div
          key={headIndex}
          onClick={() =>
            !tableData?.isLoading &&
            tableActions[head.ui_id]?.function(row, head)
          }
          // style={`${styles.textCell} ${styles[cellWidth[head?.ui_id]]}`}
          className={classes.textCell}
          style={{
            ...styles.textCell,
            width: head?.width,
            minWidth: head?.width,
          }}
        >
          {tableData?.isLoading ? (
            <SkeletonLoader
              type="rounded"
              skeletonWidth={"60%"}
              skeletonHeight={20}
            />
          ) : (
            <TypographyInline
              isEllipses
              label={
                row[head.id]?.toString().length
                  ? head?.isCapitalize
                    ? capitalizeFirstLetter(row[head.id] || "")
                    : Array.isArray(row[head.id])
                    ? row[head.id]?.join()
                    : row[head.id]
                  : "--"
              }
              size="sm"
              color={GREY.SECONDARY}
              fontWeight={fontWeight.REGULAR}
            />
          )}

          {/* copy icon code */}
          {row[head.id]?.toString().length && head?.copy ? (
            isCopied === row[head.id] &&
            rowIndex === clickedRowIndex &&
            headIndex === clickedHeadIndex ? (
              <div
                id={classes.copiedTextContainer}
                style={styles.copiedTextContainer}
              >
                <IconComponent
                  fontSize={fontSize.SM}
                  color={SUCCESS.W_500}
                  iconLabel={ICON_LABELS.DONE}
                />
                <TypographyInline
                  isEllipses
                  color={SUCCESS.W_500}
                  fontWeight={fontWeight.MEDIUM}
                  label={STRINGS.COPIED_LABEL}
                  size={"xs"}
                />
              </div>
            ) : (
              <>
                {row[head.id]?.toString().length && head?.copy ? (
                  <IconComponent
                    id={classes.copiedTextContainer}
                    color={GREY.TERTIARY}
                    fontSize={fontSize.MD}
                    iconLabel={ICON_LABELS.CONTENT_COPY}
                    onClick={() =>
                      copyTextToClipboard(row, head, headIndex, rowIndex)
                    }
                  />
                ) : null}
              </>
            )
          ) : null}
          {/* copy icon code */}
        </div>
      </CustomTooltip>
    );
  };

  const cursorTextDesign = (row, head, headIndex, rowIndex) => {
    return (
      <CustomTooltip
        key={headIndex}
        title={
          !tableData?.isLoading &&
          (window.innerWidth < 1250 || row[head.id]?.length > 20
            ? row[head.id]
            : "")
        }
      >
        <div
          key={headIndex}
          onClick={() =>
            !tableData?.isLoading &&
            tableActions[head.ui_id]?.function(row, head)
          }
          // style={`${styles.textCell} ${styles[cellWidth[head?.ui_id]]}`}
          className={classes.textCellAttendee}
          style={{
            ...styles.textCellAttendee,
            width: head?.width,
            minWidth: head?.width,
          }}
        >
          {tableData?.isLoading ? (
            <SkeletonLoader
              type="rounded"
              skeletonWidth={"60%"}
              skeletonHeight={20}
            />
          ) : (
            <TypographyInline
              isEllipses
              label={row[head.id]?.toString().length ? row[head.id] : "--"}
              size="sm"
              color={GREY.SECONDARY}
              fontWeight={fontWeight.REGULAR}
            />
          )}

          {/* copy icon code */}
          {row[head.id]?.toString().length && head?.copy ? (
            isCopied === row[head.id] &&
            rowIndex === clickedRowIndex &&
            headIndex === clickedHeadIndex ? (
              <div
                id={classes.copiedTextContainer}
                style={styles.copiedTextContainer}
              >
                <IconComponent
                  fontSize={fontSize.SM}
                  color={SUCCESS.W_500}
                  iconLabel={ICON_LABELS.DONE}
                />
                <TypographyInline
                  isEllipses
                  color={SUCCESS.W_500}
                  fontWeight={fontWeight.MEDIUM}
                  label={STRINGS.COPIED_LABEL}
                  size={"xs"}
                />
              </div>
            ) : (
              <>
                {row[head.id]?.toString().length && head?.copy ? (
                  <IconComponent
                    id={classes.copiedTextContainer}
                    color={GREY.TERTIARY}
                    fontSize={fontSize.MD}
                    iconLabel={ICON_LABELS.CONTENT_COPY}
                    onClick={() =>
                      copyTextToClipboard(row, head, headIndex, rowIndex)
                    }
                  />
                ) : null}
              </>
            )
          ) : null}
          {/* copy icon code */}
        </div>
      </CustomTooltip>
    );
  };

  const boldTextDesign = (row, head, headIndex, rowIndex) => {
    return (
      <CustomTooltip
        key={headIndex}
        title={
          !tableData?.isLoading &&
          (window.innerWidth < 1250 || row[head.id]?.length > 20
            ? row[head.id]
            : "")
        }
      >
        <div
          key={headIndex}
          className={classes.boldTextCell}
          style={{
            ...{
              ...styles.boldTextCell,
              width: head?.width,
              minWidth: head?.width,
            },
            ...(tableData?.isSticky && styles.stickyUserInfoCell),
          }}
        >
          {!selectionPermission && (
            <Checkbox
              checked={checkedValue?.indexOf(row._id) > -1}
              value={row._id}
              disabled={
                !(
                  !disableKey?.length ||
                  (disableKey?.length && row[disableKey])
                )
              }
              onChange={checkedHandler}
            />
          )}
          {tableData?.isLoading ? (
            <SkeletonLoader
              type="rounded"
              skeletonWidth={"60%"}
              skeletonHeight={20}
            />
          ) : (
            <div
              onClick={() =>
                !tableData?.isLoading &&
                tableActions[head.ui_id]?.function(row, head)
              }
            >
              <TypographyInline
                isEllipses
                label={textEllipsesHandler(row[head.id] || "--", 20)}
                size="sm"
                color={GREY.SECONDARY}
                fontWeight={fontWeight.SEMIBOLD}
              />{" "}
            </div>
          )}

          {/* copy icon code */}
          {row[head.id]?.toString()?.length && head?.copy ? (
            isCopied === row[head.id] &&
            rowIndex === clickedRowIndex &&
            headIndex === clickedHeadIndex ? (
              <div
                id={classes.copiedTextContainer}
                style={styles.copiedTextContainer}
              >
                <IconComponent
                  fontSize={fontSize.SM}
                  color={SUCCESS.W_500}
                  iconLabel={ICON_LABELS.DONE}
                />
                <TypographyInline
                  isEllipses
                  color={SUCCESS.W_500}
                  fontWeight={fontWeight.MEDIUM}
                  label={STRINGS.COPIED_LABEL}
                  size={"xs"}
                />
              </div>
            ) : (
              <>
                {row[head.id]?.toString()?.length && head?.copy ? (
                  <IconComponent
                    id={classes.copiedTextContainer}
                    color={GREY.TERTIARY}
                    fontSize={fontSize.MD}
                    iconLabel={ICON_LABELS.CONTENT_COPY}
                    onClick={() =>
                      copyTextToClipboard(row, head, headIndex, rowIndex)
                    }
                  />
                ) : null}
              </>
            )
          ) : null}
          {/* copy icon code */}
        </div>
      </CustomTooltip>
    );
  };

  const timeStampDesign = (row, head, headIndex, rowIndex) => {
    const timeStampLabel = getTimeStampLabel(row, head);
    return (
      <CustomTooltip
        title={
          !tableData?.isLoading &&
          (window.innerWidth < 1250 || timeStampLabel?.length > 20
            ? timeStampLabel
            : "")
        }
      >
        <div
          key={headIndex}
          onClick={() => tableActions[head.ui_id]?.function(row, head)}
          className={classes.textCell}
          style={{
            ...styles.textCell,
            width: head?.width,
            minWidth: head?.width,
          }}
        >
          {tableData?.isLoading ? (
            <SkeletonLoader
              type="rounded"
              skeletonWidth={"60%"}
              skeletonHeight={20}
            />
          ) : (
            <TypographyInline
              isEllipses
              label={timeStampLabel}
              size="sm"
              color={GREY.SECONDARY}
              fontWeight={fontWeight.REGULAR}
            />
          )}

          {/* copy icon code */}
          {timeStampLabel?.length && timeStampLabel !== "--" && head?.copy ? (
            isCopied === row[head.id] &&
            rowIndex === clickedRowIndex &&
            headIndex === clickedHeadIndex ? (
              <div
                id={classes.copiedTextContainer}
                style={styles.copiedTextContainer}
              >
                <IconComponent
                  fontSize={fontSize.SM}
                  color={SUCCESS.W_500}
                  iconLabel={ICON_LABELS.DONE}
                />
                <TypographyInline
                  isEllipses
                  color={SUCCESS.W_500}
                  fontWeight={fontWeight.MEDIUM}
                  label={STRINGS.COPIED_LABEL}
                  size={"xs"}
                />
              </div>
            ) : (
              <>
                {timeStampLabel?.length &&
                timeStampLabel !== "--" &&
                head?.copy ? (
                  <IconComponent
                    id={classes.copiedTextContainer}
                    color={GREY.TERTIARY}
                    fontSize={fontSize.MD}
                    iconLabel={ICON_LABELS.CONTENT_COPY}
                    onClick={() =>
                      copyTextToClipboard(row, head, headIndex, rowIndex)
                    }
                  />
                ) : null}
              </>
            )
          ) : null}
          {/* copy icon code */}
        </div>
      </CustomTooltip>
    );
  };

  // const userInfoDesign = (row, head, headIndex, rowIndex) => {
  //   return (
  //     <div
  //       key={headIndex}
  //       className={classes.userInfoCell}
  //       style={{
  //         ...{
  //           ...(selectionPermission
  //             ? {
  //                 ...styles.userInfoCellWoChkbox,
  //                 width: head?.width,
  //                 minWidth: head?.width,
  //               }
  //             : {
  //                 ...styles.userInfoCell,
  //                 width: head?.width,
  //                 minWidth: head?.width,
  //               }),
  //         },
  //         ...(tableData?.isSticky && styles.stickyUserInfoCell),
  //       }}
  //     >
  //       <div style={styles.checkboxAvatarContainer}>
  //         {!selectionPermission && (
  //           <div
  //             // style={` ${styles.checkboxContainer} ${tableData.isSticky && styles.stickyRowCheckbox
  //             //   }`}
  //             style={
  //               tableData?.isSticky
  //                 ? { ...styles.stickyRowCheckbox, ...styles.checkboxContainer }
  //                 : { ...styles.checkboxContainer }
  //             }
  //           >
  //             <Checkbox
  //               checked={
  //                 tableHeaderStripValue?.length ||
  //                 checkedValue?.indexOf(row._id) > -1
  //               }
  //               value={row._id}
  //               disabled={
  //                 !(
  //                   !disableKey?.length ||
  //                   (disableKey?.length && row[disableKey])
  //                 )
  //               }
  //               onChange={checkedHandler}
  //             />
  //           </div>
  //         )}

  //         {tableData?.isLoading ? (
  //           <SkeletonLoader
  //             type="circular"
  //             skeletonWidth={32}
  //             skeletonHeight={32}
  //           />
  //         ) : (
  //           <>
  //             <AvatarNameWithLink row={row} />
  //           </>
  //         )}
  //       </div>
  //       <CustomTooltip
  //         title={
  //           !tableData?.isLoading &&
  //           (window.innerWidth < 1250 ||
  //           `${row?.first_name} ${row?.last_name}`?.length > 20
  //             ? `${row?.first_name || ""} ${row?.last_name || ""}`
  //             : "")
  //         }
  //       >
  //         <div style={styles.userName}>
  //           {tableData?.isLoading ? (
  //             <SkeletonLoader
  //               type="rounded"
  //               skeletonWidth={"60%"}
  //               skeletonHeight={20}
  //             />
  //           ) : (
  //             <TypographyInline
  //               isEllipses
  //               label={
  //                 row?.first_name?.length || row?.last_name?.length
  //                   ? `${row?.first_name?.length ? row?.first_name : ""} ${
  //                       row?.last_name ? row?.last_name : ""
  //                     }`
  //                   : "--"
  //               }
  //               size="sm"
  //               fontWeight={fontWeight.SEMIBOLD}
  //               color={GREY.SECONDARY}
  //               onClick={() => {
  //                 !tableData?.isLoading &&
  //                   tableActions[head.ui_id]?.function(
  //                     row,
  //                     head,
  //                     headIndex,
  //                     rowIndex
  //                   );
  //                 // }
  //               }}
  //             />
  //           )}

  //           {/* copy icon code */}
  //           {row?.first_name?.length && row?.last_name?.length && head?.copy ? (
  //             isCopied === `${row?.first_name} ${row?.last_name}` &&
  //             rowIndex === clickedRowIndex &&
  //             headIndex === clickedHeadIndex ? (
  //               <div
  //                 id={classes.copiedTextContainer}
  //                 style={styles.copiedTextContainer}
  //               >
  //                 <IconComponent
  //                   fontSize={fontSize.SM}
  //                   color={SUCCESS.W_500}
  //                   iconLabel={ICON_LABELS.DONE}
  //                 />
  //                 <TypographyInline
  //                   isEllipses
  //                   color={SUCCESS.W_500}
  //                   fontWeight={fontWeight.MEDIUM}
  //                   label={STRINGS.COPIED_LABEL}
  //                   size={"xs"}
  //                 />
  //               </div>
  //             ) : (
  //               <>
  //                 {row?.first_name?.length &&
  //                 row?.last_name?.length &&
  //                 head?.copy ? (
  //                   <IconComponent
  //                     id={classes.copiedTextContainer}
  //                     color={GREY.TERTIARY}
  //                     fontSize={fontSize.MD}
  //                     iconLabel={ICON_LABELS.CONTENT_COPY}
  //                     onClick={() =>
  //                       copyTextToClipboard(
  //                         row,
  //                         head,
  //                         headIndex,
  //                         rowIndex,
  //                         `${row?.first_name} ${row?.last_name}`
  //                       )
  //                     }
  //                   />
  //                 ) : null}
  //               </>
  //             )
  //           ) : null}
  //           {/* copy icon code */}
  //         </div>
  //       </CustomTooltip>
  //     </div>
  //   );
  // };

  const userInfoDesign = (row, head, headIndex, rowIndex) => {
    // Determine display name based only on name, first_name, and last_name
    const hasPrimaryNameField = row?.name?.length > 0;
    const displayName =
      row?.name?.length > 0
        ? row.name
        : row?.first_name?.length || row?.last_name?.length
        ? `${row?.first_name?.length ? row?.first_name : ""} ${
            row?.last_name ? row?.last_name : ""
          }`.trim()
        : "--";

    return (
      <div
        key={headIndex}
        className={classes.userInfoCell}
        style={{
          ...(selectionPermission
            ? {
                ...styles.userInfoCellWoChkbox,
                width: head?.width,
                minWidth: head?.width,
              }
            : {
                ...styles.userInfoCell,
                width: head?.width,
                minWidth: head?.width,
              }),
          ...(tableData?.isSticky && styles.stickyUserInfoCell),
        }}
      >
        <div style={styles.checkboxAvatarContainer}>
          {!selectionPermission && (
            <div
              style={
                tableData?.isSticky
                  ? { ...styles.stickyRowCheckbox, ...styles.checkboxContainer }
                  : { ...styles.checkboxContainer }
              }
            >
              <Checkbox
                checked={
                  tableHeaderStripValue?.length ||
                  checkedValue?.indexOf(row._id) > -1
                }
                value={row._id}
                disabled={
                  !(
                    !disableKey?.length ||
                    (disableKey?.length && row[disableKey])
                  )
                }
                onChange={checkedHandler}
              />
            </div>
          )}

          {tableData?.isLoading ? (
            <SkeletonLoader
              type="circular"
              skeletonWidth={32}
              skeletonHeight={32}
            />
          ) : (
            <>
              {/* Only show avatar if no name */}
              {!hasPrimaryNameField && <AvatarNameWithLink row={row} />}
            </>
          )}
        </div>

        <CustomTooltip
          title={
            !tableData?.isLoading &&
            (window.innerWidth < 1250 || displayName?.length > 20
              ? displayName
              : "")
          }
        >
          <div style={styles.userName}>
            {/* --- ADDED FOLDER ICON LOGIC START --- */}
            {row.type === "folder" && (
              <>
                {tableData?.isLoading ? (
                  <SkeletonLoader
                    type="rounded"
                    skeletonWidth={18}
                    skeletonHeight={18}
                  />
                ) : (
                  <IconComponent
                    color={WARNING.W_500}
                    fontSize={fontSize.LG}
                    iconLabel={ICON_LABELS.FOLDER}
                  />
                )}
              </>
            )}
            {/* --- ADDED FOLDER ICON LOGIC END --- */}

            {tableData?.isLoading ? (
              <SkeletonLoader
                type="rounded"
                skeletonWidth={"60%"}
                skeletonHeight={20}
              />
            ) : (
              <TypographyInline
                isEllipses
                label={displayName}
                size="sm"
                fontWeight={fontWeight.SEMIBOLD}
                color={GREY.SECONDARY}
                onClick={() => {
                  !tableData?.isLoading &&
                    tableActions[head.ui_id]?.function(
                      row,
                      head,
                      headIndex,
                      rowIndex
                    );
                }}
              />
            )}

            {/* copy icon logic */}
            {(hasPrimaryNameField ||
              (row?.first_name?.length && row?.last_name?.length)) &&
            head?.copy ? (
              isCopied === displayName &&
              rowIndex === clickedRowIndex &&
              headIndex === clickedHeadIndex ? (
                <div
                  id={classes.copiedTextContainer}
                  style={styles.copiedTextContainer}
                >
                  <IconComponent
                    fontSize={fontSize.SM}
                    color={SUCCESS.W_500}
                    iconLabel={ICON_LABELS.DONE}
                  />
                  <TypographyInline
                    isEllipses
                    color={SUCCESS.W_500}
                    fontWeight={fontWeight.MEDIUM}
                    label={STRINGS.COPIED_LABEL}
                    size={"xs"}
                  />
                </div>
              ) : (
                <IconComponent
                  id={classes.copiedTextContainer}
                  color={GREY.TERTIARY}
                  fontSize={fontSize.MD}
                  iconLabel={ICON_LABELS.CONTENT_COPY}
                  onClick={() =>
                    copyTextToClipboard(
                      row,
                      head,
                      headIndex,
                      rowIndex,
                      displayName
                    )
                  }
                />
              )
            ) : null}
          </div>
        </CustomTooltip>
      </div>
    );
  };

  const companyInfoDesign = (row, head, headIndex, rowIndex) => {
    return (
      <div
        key={headIndex}
        // style={`${selectionPermission
        //   ? styles.userInfoCellWoChkbox
        //   : styles.userInfoCell
        //   } ${styles[cellWidth[head.ui_id]]}  ${tableData.isSticky && !headIndex && styles.stickyUserInfoCell
        //   } ${headIndex && styles.comapnyCellPadding} `}
        className={classes.userInfoCell}
        style={{
          ...{
            ...(selectionPermission
              ? {
                  ...styles.userInfoCellWoChkbox,
                  width: head?.width,
                  minWidth: head?.width,
                }
              : {
                  ...styles.userInfoCell,
                  width: head?.width,
                  minWidth: head?.width,
                }),
          },
          ...(tableData?.isSticky && !headIndex && styles.stickyUserInfoCell),
          ...(headIndex && styles.comapnyCellPadding),
        }}
      >
        <div style={styles.checkboxAvatarContainer}>
          {!selectionPermission && !headIndex && (
            <div
              // style={` ${styles.checkboxContainer} ${tableData.isSticky && styles.stickyRowCheckbox
              //   }`}
              style={
                tableData?.isSticky
                  ? { ...styles.stickyRowCheckbox, ...styles.checkboxContainer }
                  : { ...styles.checkboxContainer }
              }
            >
              <Checkbox
                checked={
                  tableHeaderStripValue?.length ||
                  checkedValue?.indexOf(row._id) > -1
                }
                value={row._id}
                disabled={
                  !(
                    !disableKey?.length ||
                    (disableKey?.length && row[disableKey])
                  )
                }
                onChange={checkedHandler}
              />
            </div>
          )}

          {tableData?.isLoading ? (
            <SkeletonLoader
              type="circular"
              skeletonWidth={32}
              skeletonHeight={32}
            />
          ) : (
            <AvatarOutlined size={"small"}>
              <Image
                onError={(e) => {
                  e.target.src = DUMMY_COMPANY_IMAGE;
                }}
                src={createCompanyURL(
                  row["company_website"] || DUMMY_COMPANY_IMAGE
                )}
                style={styles.companyIcon}
                alt=""
              />
            </AvatarOutlined>
          )}
        </div>
        <CustomTooltip
          title={
            !tableData?.isLoading &&
            (window.innerWidth < 1250 || `${row[head.id]}`?.length > 15
              ? `${row[head.id]}`
              : "")
          }
        >
          <div style={styles.userName}>
            {tableData?.isLoading ? (
              <SkeletonLoader
                type="rounded"
                skeletonWidth={"60%"}
                skeletonHeight={20}
              />
            ) : (
              <>
                {!headIndex ? (
                  <TypographyInline
                    isEllipses
                    label={row[head.id]?.length ? `${row[head.id]}` : "--"}
                    size="sm"
                    fontWeight={fontWeight.SEMIBOLD}
                    color={GREY.SECONDARY}
                    onClick={() => {
                      !tableData?.isLoading &&
                        tableActions[head.ui_id]?.function(
                          row,
                          head,
                          headIndex,
                          rowIndex
                        );
                    }}
                  />
                ) : (
                  <Anchor
                    href={row["company_website"]}
                    target="_blank"
                    style={styles.companyNameLink}
                  >
                    <TypographyInline
                      isEllipses
                      label={
                        row[head.id]?.length
                          ? textEllipsesHandler(row[head.id], 15)
                          : "--"
                      }
                      color={GREY.SECONDARY}
                      size={"sm"}
                      fontWeight={fontWeight.REGULAR}
                    />
                  </Anchor>
                )}
              </>
            )}

            {/* copy icon code */}
            {row[head.id]?.length && head?.copy ? (
              isCopied === row[head.id] &&
              rowIndex === clickedRowIndex &&
              headIndex === clickedHeadIndex ? (
                <div
                  id={classes.copiedTextContainer}
                  style={styles.copiedTextContainer}
                >
                  <IconComponent
                    fontSize={fontSize.SM}
                    color={SUCCESS.W_500}
                    iconLabel={ICON_LABELS.DONE}
                  />
                  <TypographyInline
                    isEllipses
                    color={SUCCESS.W_500}
                    fontWeight={fontWeight.MEDIUM}
                    label={STRINGS.COPIED_LABEL}
                    size={"xs"}
                  />
                </div>
              ) : (
                <>
                  {row[head.id]?.length && head?.copy ? (
                    <IconComponent
                      id={classes.copiedTextContainer}
                      color={GREY.TERTIARY}
                      fontSize={fontSize.MD}
                      iconLabel={ICON_LABELS.CONTENT_COPY}
                      onClick={() =>
                        copyTextToClipboard(row, head, headIndex, rowIndex)
                      }
                    />
                  ) : null}
                </>
              )
            ) : null}
            {/* copy icon code */}
          </div>
        </CustomTooltip>
      </div>
    );
  };

  const urlDesign = (row, head, headIndex, rowIndex) => {
    return (
      <Anchor
        href={row[head.id]}
        target="_blank"
        style={styles.companyNameLink}
        className={classes.companyNameLink}
        key={headIndex}
      >
        {tableData?.isLoading ? (
          <SkeletonLoader
            type="rounded"
            skeletonWidth={"60%"}
            skeletonHeight={20}
          />
        ) : (
          <TypographyInline
            isEllipses
            label={textEllipsesHandler(row[head.id], 25)}
            color={GREY.SECONDARY}
            size={"sm"}
            fontWeight={fontWeight.REGULAR}
          />
        )}

        {/* copy icon code */}
        {row[head.id]?.length && head?.copy ? (
          isCopied === row[head.id] &&
          rowIndex === clickedRowIndex &&
          headIndex === clickedHeadIndex ? (
            <div
              id={classes.copiedTextContainer}
              style={styles.copiedTextContainer}
            >
              <IconComponent
                fontSize={fontSize.SM}
                color={SUCCESS.W_500}
                iconLabel={ICON_LABELS.DONE}
              />
              <TypographyInline
                isEllipses
                color={SUCCESS.W_500}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.COPIED_LABEL}
                size={"xs"}
              />
            </div>
          ) : (
            <>
              {row[head.id]?.length && head?.copy ? (
                <IconComponent
                  id={classes.copiedTextContainer}
                  color={GREY.TERTIARY}
                  fontSize={fontSize.MD}
                  iconLabel={ICON_LABELS.CONTENT_COPY}
                  onClick={() =>
                    copyTextToClipboard(row, head, headIndex, rowIndex)
                  }
                />
              ) : null}
            </>
          )
        ) : null}
        {/* copy icon code */}
      </Anchor>
    );
  };

  const actionIconDesign = (row, head, headIndex, rowIndex) => {
    return (
      <>
        {tableOperations?.length < 3 ? (
          <div
            key={headIndex}
            style={{
              ...styles.actionIconCellPaddingContainer,
              width: head?.width,
              minWidth: head?.width,
            }}
          >
            {tableOperations?.map((item, headIndex) => {
              return (
                <>
                  {tableData?.isLoading ? (
                    <SkeletonLoader
                      type="rounded"
                      skeletonWidth={36}
                      skeletonHeight={36}
                      stackWidth={36}
                    />
                  ) : (
                    <CustomTooltip title={!tableData?.isLoading && item.label}>
                      <div
                        key={headIndex}
                        onClick={() =>
                          (!disableKey?.length ||
                            (disableKey?.length && row[disableKey])) &&
                          item?.function(row, head)
                        }
                        style={
                          disableKey?.length && !row[disableKey]
                            ? styles.actionIconsDisabledContainer
                            : styles.actionIconsContainer
                        }
                      >
                        <IconComponent
                          disabled={disableKey?.length && !row[disableKey]}
                          color={
                            disableKey?.length && !row[disableKey]
                              ? GREY.DISABLED
                              : GREY.TERTIARY
                          }
                          fontSize={fontSize.MD}
                          iconLabel={item?.iconLabel}
                        />
                      </div>
                    </CustomTooltip>
                  )}
                </>
              );
            })}
          </div>
        ) : (
          <div
            key={headIndex}
            // style={`${styles.actionCellContainer} ${styles[cellWidth[head?.ui_id]]
            //   } ${disableKey?.length && !row[disableKey]
            //     ? styles.actionIconsDisabled
            //     : ""
            //   }`}
            style={
              disableKey?.length && !row[disableKey]
                ? {
                    ...styles.actionIconsDisabled,
                    ...styles.actionCellContainer,
                    width: head?.width,
                    minWidth: head?.width,
                  }
                : {
                    ...styles.actionCellContainer,
                    ...styles.cursPointr,
                    width: head?.width,
                    minWidth: head?.width,
                  }
            }
            onClick={(event) => {
              if (
                !disableKey?.length ||
                (disableKey?.length && row[disableKey])
              ) {
                tableActions[head?.ui_id]?.function(
                  row,
                  head,
                  headIndex,
                  rowIndex
                );
                actionIconHandler(event, row, head);
              }
            }}
          >
            {tableData?.isLoading ? (
              <SkeletonLoader
                type="rounded"
                skeletonWidth={5}
                skeletonHeight={15}
                stackWidth={5}
              />
            ) : (
              <IconComponent
                iconLabel={ICON_LABELS.MORE_VERT}
                color={
                  disableKey?.length && !row[disableKey]
                    ? BORDER.LIGHT
                    : GREY.SECONDARY
                }
                fontSize={"20px"}
              />
            )}
          </div>
        )}
      </>
    );
  };

  const defaultAction = (row, head, headIndex, rowIndex) => {
    return <div key={headIndex}></div>;
  };

  const emailDesign = (row, head, headIndex, rowIndex) => {
    return (
      <div
        key={headIndex}
        // style={`${row[head.id] === false ? styles.emailCell : styles.emailTextCell
        //   } ${styles.emailCellWidth}`}
        className={classes.emailCell}
        style={
          row[head.id] === false
            ? { ...styles.emailCell, width: head?.width, minWidth: head?.width }
            : {
                ...styles.emailTextCell,
                width: head?.width,
                minWidth: head?.width,
              }
        }
      >
        {row[head.id] === false ? (
          <AvatarOutlined
            onClick={() => {
              !tableData?.isLoading &&
                tableActions[head.ui_id]?.function(
                  row,
                  head,
                  headIndex,
                  rowIndex
                );
            }}
          >
            {tableActions[head.id]?.loading === row?._id ? (
              <SkeletonLoader
                type="circular"
                skeletonHeight="32px"
                skeletonWidth="32px"
              />
            ) : (
              <IconComponent
                iconLabel={"mail_lock"}
                color={GREY.TERTIARY}
                fontSize={"18px"}
              />
            )}
          </AvatarOutlined>
        ) : (
          <CustomTooltip
            title={
              !tableData?.isLoading &&
              (window.innerWidth < 1250 || row[head.id]?.length > 20
                ? row[head.id]
                : "")
            }
          >
            <div style={styles.emailCopiedTextCellContainer}>
              {tableData?.isLoading ? (
                <SkeletonLoader
                  type="rounded"
                  skeletonWidth={"60%"}
                  skeletonHeight={16}
                />
              ) : (
                <TypographyInline
                  isEllipses
                  label={row[head.id] || "--"}
                  size="sm"
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                />
              )}

              {/* copy icon code */}
              {isCopied === row[head.id] &&
              head?.copy &&
              rowIndex === clickedRowIndex &&
              headIndex === clickedHeadIndex ? (
                <div
                  id={classes.copiedTextContainer}
                  style={styles.copiedTextContainer}
                >
                  <IconComponent
                    fontSize={fontSize.SM}
                    color={SUCCESS.W_500}
                    iconLabel={ICON_LABELS.DONE}
                  />
                  <TypographyInline
                    isEllipses
                    color={SUCCESS.W_500}
                    fontWeight={fontWeight.MEDIUM}
                    label={STRINGS.COPIED_LABEL}
                    size={"xs"}
                  />
                </div>
              ) : (
                <>
                  {row[head.id] && head?.copy && (
                    <IconComponent
                      id={classes.copiedTextContainer}
                      color={GREY.TERTIARY}
                      fontSize={fontSize.MD}
                      iconLabel={ICON_LABELS.CONTENT_COPY}
                      onClick={() =>
                        copyTextToClipboard(row, head, headIndex, rowIndex)
                      }
                    />
                  )}
                </>
              )}
              {/* copy icon code */}
            </div>
          </CustomTooltip>
        )}
      </div>
    );
  };

  const marketingListDesign = (row, head, headIndex, rowIndex) => {
    return (
      <CustomTooltip
        title={
          !tableData?.isLoading &&
          (window.innerWidth < 1250 || row[head.id]?.length > 20
            ? row[head.id]
            : "")
        }
      >
        <div
          key={headIndex}
          className={classes.marketingListCell}
          style={{
            ...styles.marketingListCell,
            width: head?.width,
            minWidth: head?.width,
          }}
        >
          {parseInt(row?.pin) ? (
            <div style={styles.pinContainer}>
              <Image style={styles.pinIcon} src={PIN_ICON} />
            </div>
          ) : null}
          {!selectionPermission && (
            <div style={styles.checkboxContainer}>
              <Checkbox
                checked={checkedValue.indexOf(row._id) > -1}
                value={row._id}
                disabled={
                  !(
                    !disableKey?.length ||
                    (disableKey?.length && row[disableKey])
                  )
                }
                onChange={checkedHandler}
              />
            </div>
          )}
          <div
            style={
              disableKey?.length && head?.isClickAllowed
                ? row?.[disableKey]
                  ? styles.marketingListName
                  : styles.marketingListNameNotAllowed
                : styles.marketingListName
            }
            onClick={() => {
              !tableData?.isLoading &&
                tableActions[head.ui_id]?.function(row, head);
            }}
          >
            {row.type === "folder" && (
              <>
                {tableData?.isLoading ? (
                  <SkeletonLoader
                    type="rounded"
                    skeletonWidth={18}
                    skeletonHeight={18}
                  />
                ) : (
                  <IconComponent
                    color={WARNING.W_500}
                    fontSize={fontSize.LG}
                    iconLabel={ICON_LABELS.FOLDER}
                  />
                )}
              </>
            )}

            {tableData?.isLoading ? (
              <SkeletonLoader
                type="rounded"
                skeletonWidth={"60%"}
                skeletonHeight={20}
              />
            ) : (
              <>
                <TypographyInline
                  isEllipses
                  label={row[head.id]}
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={GREY.SECONDARY}
                />

                {/* Only for dashboard default case  */}

                {head?.id === "dashboard_name" && row.is_default_dashboard && (
                  <div style={styles.dashboardDefault}>
                    <TypographyInline
                      label={STRINGS.DEFAULT}
                      size="xs"
                      fontWeight={fontWeight.MEDIUM}
                      color={GREY.SECONDARY}
                    />
                  </div>
                )}

                {/* Only for dashboard default case  */}
              </>
            )}

            {/* copy icon code */}
            {row[head.id]?.length && head?.copy ? (
              isCopied === row[head.id] &&
              rowIndex === clickedRowIndex &&
              headIndex === clickedHeadIndex ? (
                <div
                  id={classes.copiedTextContainer}
                  style={styles.copiedTextContainer}
                >
                  <IconComponent
                    fontSize={fontSize.SM}
                    color={SUCCESS.W_500}
                    iconLabel={ICON_LABELS.DONE}
                  />
                  <TypographyInline
                    isEllipses
                    color={SUCCESS.W_500}
                    fontWeight={fontWeight.MEDIUM}
                    label={STRINGS.COPIED_LABEL}
                    size={"xs"}
                  />
                </div>
              ) : (
                <>
                  {row[head.id]?.length && head?.copy ? (
                    <IconComponent
                      id={classes.copiedTextContainer}
                      color={GREY.TERTIARY}
                      fontSize={fontSize.MD}
                      iconLabel={ICON_LABELS.CONTENT_COPY}
                      onClick={() =>
                        copyTextToClipboard(row, head, headIndex, rowIndex)
                      }
                    />
                  ) : null}
                </>
              )
            ) : null}
            {/* copy icon code */}
          </div>
        </div>
      </CustomTooltip>
    );
  };

  const textButtonDesign = (row, head, headIndex, rowIndex) => {
    return (
      <div
        key={headIndex}
        // style={`${row?.type === "file" && styles.textButtonCell} ${styles.textButtonCellWidth
        //   }`}
        className={classes.textButtonCell}
        style={
          row?.type === "file"
            ? {
                ...styles.textButtonCell,
                width: head?.width,
                minWidth: head?.width,
              }
            : { width: head?.width, minWidth: head?.width }
        }
      >
        {row?.type === "file" && (
          <>
            <div style={styles.stepsButtonContainer}>
              {tableData?.isLoading ? (
                <SkeletonLoader
                  type="rounded"
                  skeletonWidth={"60%"}
                  skeletonHeight={20}
                />
              ) : (
                <TypographyInline
                  isEllipses
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  size={"sm"}
                  label={`${row?.total_steps} Steps`}
                />
              )}
            </div>

            {tableData?.isLoading ? (
              <SkeletonLoader
                type="rounded"
                skeletonWidth={"60%"}
                skeletonHeight={36}
              />
            ) : (
              <Button
                LeftIcon={ICON_LABELS.ADD}
                label={STRINGS.STEPS}
                size={"sm36"}
                variant={"ghost"}
                onClick={() => {
                  !tableData?.isLoading &&
                    tableActions[head.ui_id]?.function(
                      row,
                      head,
                      headIndex,
                      rowIndex
                    );
                }}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const statsDataDesgin = (row, head, headIndex, rowIndex) => {
    return (
      <div
        key={headIndex}
        className={classes.statsDataCell}
        style={{
          ...styles.statsDataCell,
          cursor: head?.disable_key?.length
            ? row?.[head?.disable_key]
              ? row?.[head?.id] > 0
                ? "pointer"
                : "not-allowed"
              : "not-allowed"
            : head?.is_pointer
            ? "pointer"
            : "auto",
          width: head?.width,
          minWidth: head?.width,
        }}
        onClick={() => {
          !tableData?.isLoading && row?.[head?.disable_key]
            ? row?.[head?.id] > 0 &&
              tableActions[head.ui_id]?.function(row, head, headIndex, rowIndex)
            : tableActions[head.ui_id]?.function(
                row,
                head,
                headIndex,
                rowIndex
              );
        }}
      >
        {tableData?.isLoading ? (
          <SkeletonLoader
            type="rounded"
            skeletonWidth={"60%"}
            skeletonHeight={20}
          />
        ) : (
          <TypographyInline
            isEllipses
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            size={"sm"}
            label={
              row?.type === "folder"
                ? STRINGS.DOTS
                : row[head.id]?.toString().length
                ? row[head.id]
                : "--"
            }
          />
        )}

        {/* copy icon code */}
        {row[head.id]?.length && head?.copy ? (
          isCopied === row[head.id] &&
          rowIndex === clickedRowIndex &&
          headIndex === clickedHeadIndex ? (
            <div
              id={classes.copiedTextContainer}
              style={styles.copiedTextContainer}
            >
              <IconComponent
                fontSize={fontSize.SM}
                color={SUCCESS.W_500}
                iconLabel={ICON_LABELS.DONE}
              />
              <TypographyInline
                isEllipses
                color={SUCCESS.W_500}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.COPIED_LABEL}
                size={"xs"}
              />
            </div>
          ) : (
            <>
              {row[head.id]?.length && head?.copy ? (
                <IconComponent
                  id={classes.copiedTextContainer}
                  color={GREY.TERTIARY}
                  fontSize={fontSize.MD}
                  iconLabel={ICON_LABELS.CONTENT_COPY}
                  onClick={() =>
                    copyTextToClipboard(row, head, headIndex, rowIndex)
                  }
                />
              ) : null}
            </>
          )
        ) : null}
        {/* copy icon code */}
      </div>
    );
  };

  const buttonDesign = (row, head, headIndex, rowIndex) => {
    return (
      <div
        key={headIndex}
        className={classes.buttonCell}
        style={{
          ...styles.buttonCell,
          width: head?.width,
          minWidth: head?.width,
        }}
      >
        {head?.id === "export" ? (
          <>
            {tableData?.isLoading ? (
              <SkeletonLoader
                type="rounded"
                skeletonWidth={"60%"}
                skeletonHeight={20}
              />
            ) : (
              <Button
                LeftIcon={ICON_LABELS.DOWNLOAD}
                label={STRINGS.EXPORT}
                size={"sm36"}
                disabled={
                  row[disableKey] !== "completed" || row?.total_matched === 0
                }
                variant="ghost"
                onClick={() => {
                  !tableData?.isLoading &&
                    tableActions[head.ui_id]?.function(
                      row,
                      head,
                      headIndex,
                      rowIndex
                    );
                }}
              />
            )}
          </>
        ) : (
          <>
            {tableData?.isLoading ? (
              <SkeletonLoader
                type="rounded"
                skeletonWidth={"60%"}
                skeletonHeight={20}
              />
            ) : (
              <Button
                label={`${row?.total_steps} Steps`}
                size={"xs28"}
                variant={"ghost"}
                onClick={() => {
                  !tableData?.isLoading &&
                    tableActions[head.ui_id]?.function(
                      row,
                      head,
                      headIndex,
                      rowIndex
                    );
                }}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const statusDesign = (row, head, headIndex, rowIndex) => {
    return (
      <CustomTooltip title={!tableData?.isLoading && row?.state_message}>
        <div
          key={headIndex}
          className={classes.statusCell}
          style={{
            ...styles.statusCell,
            width: head?.width,
            minWidth: head?.width,
          }}
        >
          {tableData?.isLoading ? (
            <SkeletonLoader
              type="rounded"
              skeletonWidth={"60%"}
              skeletonHeight={20}
            />
          ) : row?.type !== "folder" ? (
            <StatusChip
              label={row[head?.id] || "--"}
              variant={statusVariant[row[head?.id]?.toString()?.toLowerCase()]}
            />
          ) : (
            "---"
          )}
        </div>
      </CustomTooltip>
    );
  };

  const durationTextDesign = (row, head, headIndex, rowIndex) => {
    return (
      <div
        key={headIndex}
        className={classes.durationTextCell}
        style={{
          ...styles.durationTextCell,
          width: head?.width,
          minWidth: head?.width,
        }}
      >
        {tableData?.isLoading ? (
          <SkeletonLoader
            type="rounded"
            skeletonWidth={"60%"}
            skeletonHeight={20}
          />
        ) : (
          <TypographyInline
            isEllipses
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            size={"sm"}
            label={getDurationLabel(row)}
          />
        )}

        {/* copy icon code */}
        {getDurationLabel(row)?.length && head?.copy ? (
          isCopied === getDurationLabel(row) &&
          rowIndex === clickedRowIndex &&
          headIndex === clickedHeadIndex ? (
            <div
              id={classes.copiedTextContainer}
              style={styles.copiedTextContainer}
            >
              <IconComponent
                fontSize={fontSize.SM}
                color={SUCCESS.W_500}
                iconLabel={ICON_LABELS.DONE}
              />
              <TypographyInline
                isEllipses
                color={SUCCESS.W_500}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.COPIED_LABEL}
                size={"xs"}
              />
            </div>
          ) : (
            <>
              {getDurationLabel(row)?.length && head?.copy ? (
                <IconComponent
                  id={classes.copiedTextContainer}
                  color={GREY.TERTIARY}
                  fontSize={fontSize.MD}
                  iconLabel={ICON_LABELS.CONTENT_COPY}
                  onClick={() => copyTextToClipboard(row, head)}
                />
              ) : null}
            </>
          )
        ) : null}
        {/* copy icon code */}
      </div>
    );
  };

  const textURLDesign = (row, head, headIndex, rowIndex) => {
    return (
      <div
        key={headIndex}
        className={classes.textURLCell}
        style={{
          ...styles.textURLCell,
          width: head?.width,
          minWidth: head?.width,
        }}
      >
        {tableData?.isLoading ? (
          <SkeletonLoader
            type="rounded"
            skeletonWidth={"60%"}
            skeletonHeight={20}
          />
        ) : (
          <>
            <TypographyInline
              isEllipses
              label={row[head.id]?.length ? row[head.id] : "--"}
              color={GREY.SECONDARY}
              size={"sm"}
              fontWeight={fontWeight.REGULAR}
            />
            {row[head.id]?.length ? (
              <Anchor
                href={
                  row?.schedule_page_link
                    ? `http://${row.schedule_page_link}`
                    : row[head.id]
                }
                target="_blank"
                style={styles.meetingLinkIcon}
                key={headIndex}
              >
                <IconComponent
                  iconLabel={"launch"}
                  color={PRIMARY.W_500}
                  fontSize={fontSize.SM}
                />
              </Anchor>
            ) : (
              ""
            )}

            {/* copy icon code */}
            {row[head.id]?.length && head?.copy ? (
              isCopied === row[head.id] &&
              rowIndex === clickedRowIndex &&
              headIndex === clickedHeadIndex ? (
                <div
                  id={classes.copiedTextContainer}
                  style={styles.copiedTextContainer}
                >
                  <IconComponent
                    fontSize={fontSize.SM}
                    color={SUCCESS.W_500}
                    iconLabel={ICON_LABELS.DONE}
                  />
                  <TypographyInline
                    isEllipses
                    color={SUCCESS.W_500}
                    fontWeight={fontWeight.MEDIUM}
                    label={STRINGS.COPIED_LABEL}
                    size={"xs"}
                  />
                </div>
              ) : (
                <>
                  {row[head.id]?.length && head?.copy ? (
                    <IconComponent
                      id={classes.copiedTextContainer}
                      color={GREY.TERTIARY}
                      fontSize={fontSize.MD}
                      iconLabel={ICON_LABELS.CONTENT_COPY}
                      onClick={() =>
                        copyTextToClipboard(row, head, headIndex, rowIndex)
                      }
                    />
                  ) : null}
                </>
              )
            ) : null}
            {/* copy icon code */}
          </>
        )}
      </div>
    );
  };

  const taskListDesign = (row, head, headIndex, rowIndex) => {
    return (
      <CustomTooltip
        title={
          !tableData?.isLoading &&
          (window.innerWidth < 1250 || row[head.id]?.length > 20
            ? row[head.id]
            : "")
        }
      >
        <div
          key={headIndex}
          className={classes.taskListCell}
          style={{
            ...styles.taskListCell,
            width: head?.width,
            minWidth: head?.width,
          }}
        >
          <div style={styles.checkboxContainer}>
            <Checkbox
              checked={checkedValue.indexOf(row._id) > -1}
              value={row._id}
              disabled={
                !(
                  !disableKey?.length ||
                  (disableKey?.length && row[disableKey])
                )
              }
              onChange={checkedHandler}
            />
          </div>
          <div
            style={
              row?.status || row?.is_lead
                ? styles.taskListNameWithStatus
                : styles.taskListName
            }
          >
            {tableData?.isLoading ? (
              <SkeletonLoader
                type="circular"
                skeletonWidth={18}
                skeletonHeight={18}
              />
            ) : (
              <IconComponent
                color={row?.status ? SUCCESS.W_500 : BORDER.DARK}
                fontSize={fontSize.LG}
                iconLabel={
                  row?.status
                    ? ICON_LABELS.CHECK_CIRCLE
                    : ICON_LABELS.RADIO_BUTTON_UNCHECKED
                }
                onClick={() => {
                  tableActions[head.ui_id]?.icon?.function(
                    row,
                    head,
                    headIndex,
                    rowIndex
                  );
                }}
              />
            )}
            <div>
              {tableData?.isLoading ? (
                <SkeletonLoader
                  type="rounded"
                  skeletonWidth={"60%"}
                  skeletonHeight={20}
                />
              ) : (
                <TypographyInline
                  isEllipses
                  label={textEllipsesHandler(row[head.id], 15)}
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={GREY.SECONDARY}
                  onClick={() => {
                    tableActions[head.ui_id]?.label?.function(
                      row,
                      head,
                      headIndex,
                      rowIndex
                    );
                  }}
                />
              )}
              {!row?.status && !row?.is_lead ? null : (
                <TypographyInline
                  isEllipses
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  size={"sm"}
                  label={getTaskStatusLabel(row)}
                />
              )}

              {/* copy icon code */}
              {row[head.id]?.length && head?.copy ? (
                isCopied === row[head.id] &&
                rowIndex === clickedRowIndex &&
                headIndex === clickedHeadIndex ? (
                  <div
                    id={classes.copiedTextContainer}
                    style={styles.copiedTextContainer}
                  >
                    <IconComponent
                      fontSize={fontSize.SM}
                      color={SUCCESS.W_500}
                      iconLabel={ICON_LABELS.DONE}
                    />
                    <TypographyInline
                      isEllipses
                      color={SUCCESS.W_500}
                      fontWeight={fontWeight.MEDIUM}
                      label={STRINGS.COPIED_LABEL}
                      size={"xs"}
                    />
                  </div>
                ) : (
                  <>
                    {row[head?.id]?.length && head?.copy ? (
                      <IconComponent
                        id={classes.copiedTextContainer}
                        color={GREY.TERTIARY}
                        fontSize={fontSize.MD}
                        iconLabel={ICON_LABELS.CONTENT_COPY}
                        onClick={() =>
                          copyTextToClipboard(row, head, headIndex, rowIndex)
                        }
                      />
                    ) : null}
                  </>
                )
              ) : null}
              {/* copy icon code */}
            </div>
          </div>
        </div>
      </CustomTooltip>
    );
  };

  const userDetailDesign = (row, head, headIndex, rowIndex) => {
    const capitalizeFirstName =
      row?.first_name?.charAt(0).toUpperCase() + row?.first_name?.slice(1);
    const capitalizeLastName =
      row?.last_name?.charAt(0).toUpperCase() + row?.last_name?.slice(1);
    let final_name = "";

    if (row?.first_name && !row?.last_name) {
      final_name = capitalizeFirstName;
    } else if (!row?.first_name && row?.last_name) {
      final_name = capitalizeLastName;
    } else if (row?.first_name && row?.last_name) {
      final_name = `${capitalizeFirstName} ${capitalizeLastName}`;
    }

    return (
      <div
        key={headIndex}
        className={classes.userDetailCell}
        style={{
          ...styles.userDetailCell,
          width: head?.width,
          minWidth: head?.width,
        }}
      >
        {tableData?.isLoading ? (
          <SkeletonLoader
            type="circular"
            skeletonWidth={32}
            skeletonHeight={32}
            stackWidth={32}
          />
        ) : (
          (final_name?.length || row[head?.id]?.length) && (
            <AvatarName
              name={row[head?.id]?.length ? row[head?.id] : final_name}
              size={"small"}
            />
          )
        )}
        {tableData?.isLoading ? (
          <SkeletonLoader
            type="rounded"
            skeletonWidth={"60%"}
            skeletonHeight={20}
          />
        ) : (
          <TypographyInline
            isEllipses
            label={
              final_name?.length || row[head?.id]?.length
                ? row[head?.id]?.length
                  ? row[head?.id]
                  : final_name
                : "--"
            }
            color={GREY.SECONDARY}
            size={"sm"}
            fontWeight={fontWeight.REGULAR}
          />
        )}

        {/* copy icon code */}
        {(final_name?.length || row[head?.id]?.length) && head?.copy ? (
          isCopied === final_name &&
          rowIndex === clickedRowIndex &&
          headIndex === clickedHeadIndex ? (
            <div
              id={classes.copiedTextContainer}
              style={styles.copiedTextContainer}
            >
              <IconComponent
                fontSize={fontSize.SM}
                color={SUCCESS.W_500}
                iconLabel={ICON_LABELS.DONE}
              />
              <TypographyInline
                isEllipses
                color={SUCCESS.W_500}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.COPIED_LABEL}
                size={"xs"}
              />
            </div>
          ) : (
            <>
              {final_name?.length && head?.copy ? (
                <IconComponent
                  id={classes.copiedTextContainer}
                  color={GREY.TERTIARY}
                  fontSize={fontSize.MD}
                  iconLabel={ICON_LABELS.CONTENT_COPY}
                  onClick={() =>
                    copyTextToClipboard(row, head, headIndex, rowIndex)
                  }
                />
              ) : null}
            </>
          )
        ) : null}
        {/* copy icon code */}
      </div>
    );
  };

  const companyDetailDesign = (row, head, headIndex, rowIndex) => {
    return (
      <div
        key={headIndex}
        style={{
          ...styles.companyDetailCell,
          width: head?.width,
          minWidth: head?.width,
        }}
      >
        {tableData?.isLoading ? (
          <SkeletonLoader
            type="rounded"
            skeletonWidth={32}
            skeletonHeight={32}
          />
        ) : (
          <AvatarOutlined size={"small"}>
            <Image
              src={DUMMY_COMPANY_IMAGE}
              style={styles.companyIcon}
              alt="Company Logo"
            />
          </AvatarOutlined>
        )}
        {tableData?.isLoading ? (
          <SkeletonLoader
            type="rounded"
            skeletonWidth={"60%"}
            skeletonHeight={20}
          />
        ) : (
          <TypographyInline
            isEllipses
            label={row?.company_name?.length ? row?.company_name : "--"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            size={"sm"}
          />
        )}

        {/* copy icon code */}
        {row?.company_name?.length && head?.copy ? (
          isCopied === row?.company_name &&
          rowIndex === clickedRowIndex &&
          headIndex === clickedHeadIndex ? (
            <div
              id={classes.copiedTextContainer}
              style={styles.copiedTextContainer}
            >
              <IconComponent
                fontSize={fontSize.SM}
                color={SUCCESS.W_500}
                iconLabel={ICON_LABELS.DONE}
              />
              <TypographyInline
                isEllipses
                color={SUCCESS.W_500}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.COPIED_LABEL}
                size={"xs"}
              />
            </div>
          ) : (
            <>
              {row?.company_name?.length && head?.copy ? (
                <IconComponent
                  id={classes.copiedTextContainer}
                  color={GREY.TERTIARY}
                  fontSize={fontSize.MD}
                  iconLabel={ICON_LABELS.CONTENT_COPY}
                  onClick={() =>
                    copyTextToClipboard(row, head, headIndex, rowIndex)
                  }
                />
              ) : null}
            </>
          )
        ) : null}
        {/* copy icon code */}
      </div>
    );
  };

  const taskTypeDesign = (row, head, headIndex, rowIndex) => {
    return (
      <div
        key={headIndex}
        style={{
          ...styles.taskTypeCell,
          width: head?.width,
          minWidth: head?.width,
        }}
      >
        {tableData?.isLoading ? (
          <SkeletonLoader
            type="rounded"
            skeletonWidth={"60%"}
            skeletonHeight={20}
          />
        ) : (
          <>
            <IconComponent
              iconLabel={TASK_TYPE[row?.step_type]?.icon}
              fontSize={fontSize.LG}
              color={TASK_TYPE[row?.step_type]?.iconColor}
            />
            <TypographyInline
              isEllipses
              label={TASK_TYPE[row?.step_type]?.label}
              color={GREY.SECONDARY}
              fontWeight={fontWeight.REGULAR}
              size={"sm"}
            />
          </>
        )}

        {/* copy icon code */}
        {TASK_TYPE[row?.step_type]?.label?.length && head?.copy ? (
          isCopied === TASK_TYPE[row?.step_type]?.label &&
          rowIndex === clickedRowIndex &&
          headIndex === clickedHeadIndex ? (
            <div
              id={classes.copiedTextContainer}
              style={styles.copiedTextContainer}
            >
              <IconComponent
                fontSize={fontSize.SM}
                color={SUCCESS.W_500}
                iconLabel={ICON_LABELS.DONE}
              />
              <TypographyInline
                isEllipses
                color={SUCCESS.W_500}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.COPIED_LABEL}
                size={"xs"}
              />
            </div>
          ) : (
            <>
              {TASK_TYPE[row?.step_type]?.label?.length && head?.copy ? (
                <IconComponent
                  id={classes.copiedTextContainer}
                  color={GREY.TERTIARY}
                  fontSize={fontSize.MD}
                  iconLabel={ICON_LABELS.CONTENT_COPY}
                  onClick={() =>
                    copyTextToClipboard(row, head, headIndex, rowIndex)
                  }
                />
              ) : null}
            </>
          )
        ) : null}
        {/* copy icon code */}
      </div>
    );
  };

  const dueTimeStampDesign = (row, head, headIndex, rowIndex) => {
    let presentTime = new Date().getTime();

    let processingTime = row?.show_time_stamp
      ? parseInt(row?.show_time_stamp)
      : new Date(row?.show_date_time).getTime();
    let isOverdue = processingTime < presentTime;
    let processingTimeStamp = timeStampToDateTime(processingTime);
    return (
      <div
        key={headIndex}
        className={classes.dueTimeStampCell}
        style={{
          ...styles.dueTimeStampCell,
          width: head?.width,
          minWidth: head?.width,
        }}
      >
        {isOverdue && (row?.show_time_stamp || row?.show_date_time) && (
          <TypographyInline
            isEllipses
            color={PRIMARY.W_500}
            fontWeight={fontWeight.REGULAR}
            label={`${STRINGS.OVERDUE_TEXT}: `}
            size={"sm"}
          />
        )}
        {tableData?.isLoading ? (
          <SkeletonLoader
            type="rounded"
            skeletonWidth={"60%"}
            skeletonHeight={20}
          />
        ) : (
          <TypographyInline
            isEllipses
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label={
              row?.show_time_stamp || row?.show_date_time
                ? processingTimeStamp
                : "--"
            }
            size={"sm"}
          />
        )}

        {/* copy icon code */}
        {processingTimeStamp?.length && head?.copy ? (
          isCopied === processingTimeStamp &&
          rowIndex === clickedRowIndex &&
          headIndex === clickedHeadIndex ? (
            <div
              id={classes.copiedTextContainer}
              style={styles.copiedTextContainer}
            >
              <IconComponent
                fontSize={fontSize.SM}
                color={SUCCESS.W_500}
                iconLabel={ICON_LABELS.DONE}
              />
              <TypographyInline
                isEllipses
                color={SUCCESS.W_500}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.COPIED_LABEL}
                size={"xs"}
              />
            </div>
          ) : (
            <>
              {processingTimeStamp?.length && head?.copy ? (
                <IconComponent
                  id={classes.copiedTextContainer}
                  color={GREY.TERTIARY}
                  fontSize={fontSize.MD}
                  iconLabel={ICON_LABELS.CONTENT_COPY}
                  onClick={() =>
                    copyTextToClipboard(row, head, headIndex, rowIndex)
                  }
                />
              ) : null}
            </>
          )
        ) : null}
        {/* copy icon code */}
      </div>
    );
  };

  const leadStatusDesign = (row, head, headIndex, rowIndex) => {
    return (
      <div
        key={headIndex}
        className={classes.leadStatusCell}
        style={{
          ...styles.leadStatusCell,
          width: head?.width,
          minWidth: head?.width,
        }}
        onClick={(event) => {
          leadStatusHandler(event, row, head);
          !tableData?.isLoading &&
            tableActions[head.ui_id]?.function(row, head, headIndex, rowIndex);
        }}
      >
        {tableData?.isLoading ? (
          <SkeletonLoader
            type="rounded"
            skeletonWidth={"60%"}
            skeletonHeight={20}
          />
        ) : (
          <FilterChip
            label={row?.status || row[head?.id] || "--"}
            variant={row?.account_status === "active" ? "blue" : "orange"}
          />
        )}
      </div>
    );
  };

  const amountDesign = (row, head, headIndex, rowIndex) => {
    return (
      <CustomTooltip
        key={headIndex}
        title={
          !tableData?.isLoading &&
          (window.innerWidth < 1250 || row[head.id]?.length > 20
            ? `$${row[head.id]}`
            : "")
        }
      >
        <div
          key={headIndex}
          onClick={() =>
            !tableData?.isLoading &&
            tableActions[head.ui_id]?.function(row, head)
          }
          className={classes.amountCell}
          style={{
            ...styles.amountCell,
            width: head?.width,
            minWidth: head?.width,
          }}
        >
          {tableData?.isLoading ? (
            <SkeletonLoader
              type="rounded"
              skeletonWidth={"60%"}
              skeletonHeight={20}
            />
          ) : (
            <TypographyInline
              isEllipses
              label={
                row[head.id]?.toString().length
                  ? formatCurrency(row[head.id])
                  : "--"
              }
              size="sm"
              color={GREY.SECONDARY}
              fontWeight={fontWeight.REGULAR}
            />
          )}

          {/* copy icon code */}
          {row[head.id]?.toString().length && head?.copy ? (
            isCopied === row[head.id] &&
            rowIndex === clickedRowIndex &&
            headIndex === clickedHeadIndex ? (
              <div
                id={classes.copiedTextContainer}
                style={styles.copiedTextContainer}
              >
                <IconComponent
                  fontSize={fontSize.SM}
                  color={SUCCESS.W_500}
                  iconLabel={ICON_LABELS.DONE}
                />
                <TypographyInline
                  isEllipses
                  color={SUCCESS.W_500}
                  fontWeight={fontWeight.MEDIUM}
                  label={STRINGS.COPIED_LABEL}
                  size={"xs"}
                />
              </div>
            ) : (
              <>
                {row[head.id]?.toString().length && head?.copy ? (
                  <IconComponent
                    id={classes.copiedTextContainer}
                    color={GREY.TERTIARY}
                    fontSize={fontSize.MD}
                    iconLabel={ICON_LABELS.CONTENT_COPY}
                    onClick={() =>
                      copyTextToClipboard(row, head, headIndex, rowIndex)
                    }
                  />
                ) : null}
              </>
            )
          ) : null}
          {/* copy icon code */}
        </div>
      </CustomTooltip>
    );
  };

  // Object calling designs according to ui_id.
  const tableBodyObj = {
    text: textDesign,
    cursor_text: cursorTextDesign,
    bold_text: boldTextDesign,
    number_text: textDesign,
    user_info: userInfoDesign,
    company_info: companyInfoDesign,
    url: urlDesign,
    action: actionIconDesign,
    email: emailDesign,
    default: defaultAction,
    marketing_list: marketingListDesign,
    text_button: textButtonDesign,
    stats_data: statsDataDesgin,
    time_stamp: timeStampDesign,
    status: statusDesign,
    button: buttonDesign,
    duration_text: durationTextDesign,
    text_url: textURLDesign,
    task_list: taskListDesign,
    user_detail: userDetailDesign,
    company_detail: companyDetailDesign,
    task_type: taskTypeDesign,
    due_time_stamp: dueTimeStampDesign,
    lead_status: leadStatusDesign,
    amount: amountDesign,
  };

  // Status Variant used for status chip in Campaigns.
  const statusVariant = {
    "setting up": "yellow",
    scheduled: "purple",
    active: "blue",
    interrupted: "red",
    completed: "green",
    paused: "yellow",
    processing: "yellow",
    "ai processing": "blue",
    "ready to run": "green",
    inactive: "red",
    automated: "blue",
    manual: "red",
  };

  // Task Type Object having iconLabel, icon and Color of icon.
  const TASK_TYPE = {
    1: { icon: ICON_LABELS.EMAIL, label: "Email", iconColor: PRIMARY.W_500 },
    2: {
      icon: ICON_LABELS.INSERT_DRIVE_FILE,
      label: "To do",
      iconColor: INTER_DIMENSIONAL_BLUE.W_400,
    },
    3: {
      icon: ICON_LABELS.CALL,
      label: "Call",
      iconColor: SUCCESS.W_500,
    },
  };

  // Returns sort Image according to direction.
  const sortImageHandler = (head) => {
    if (table_id) {
      if (
        paginationDataSelector?.data?.[table_id]?.query?.sort?.direction ===
          "asc" &&
        head?.id === paginationDataSelector?.data?.[table_id]?.query?.sort?.id
      ) {
        return ASCENDING_SORT;
      } else if (
        paginationDataSelector?.data?.[table_id]?.query?.sort?.direction ===
          "desc" &&
        head?.id === paginationDataSelector?.data?.[table_id]?.query?.sort?.id
      ) {
        return DESCENDING_SORT;
      } else {
        return NO_SORT;
      }
    } else {
      if (
        paginationDataSelector?.data?.query?.sort?.direction === "asc" &&
        head?.id === paginationDataSelector?.data?.query?.sort?.id
      ) {
        return ASCENDING_SORT;
      } else if (
        paginationDataSelector?.data?.query?.sort?.direction === "desc" &&
        head?.id === paginationDataSelector?.data?.query?.sort?.id
      ) {
        return DESCENDING_SORT;
      } else {
        return NO_SORT;
      }
    }
  };

  React.useEffect(() => {
    if (table_id) {
      setCheckedValue(
        paginationDataSelector?.data?.[table_id]?.checkedRow || []
      );
    } else {
      setCheckedValue(paginationDataSelector?.data?.checkedRow || []);
    }
  }, [paginationDataSelector?.data?.checkedRow, table_id]);

  useEffect(() => {
    if (tableData?.isLoading) {
      const element = document.getElementById("modal_body");
      element.scrollTop = 0;
      element.scrollLeft = 0;
    }
  }, [tableData?.isLoading]);

  return (
    <div style={styles.tableMainContainer}>
      {/* Table */}
      <div id="modal_body" style={styles.table}>
        {/* Table Head */}
        {checkedRowSelect?.length > 0 && headerStrip?.length ? (
          //  Table Head when all checkbox are checked.
          <div style={styles.tableHeadAllChecked}>
            <div style={styles.selectedAllLabel}>
              {!selectionPermission && (
                <Checkbox
                  partial={
                    tableHeaderStripValue?.length
                      ? false
                      : checkedRowSelect?.length &&
                        checkedRowSelect?.length < tableBody?.length
                  }
                  checked={
                    tableHeaderStripValue?.length ||
                    checkedRowSelect?.length === tableBody?.length
                  }
                  onChange={checkedAllHandler}
                />
              )}

              <TypographyInline
                isEllipses
                color={PRIMARY.W_500}
                fontWeight={fontWeight.MEDIUM}
                label={`${
                  tableHeaderStripValue?.length
                    ? tableHeaderStripValue
                    : table_id
                    ? paginationDataSelector?.data?.[table_id]?.checkedRow
                        ?.length
                    : checkedRowSelect?.length
                } Selected`}
                size="sm"
              />
            </div>
            <div style={styles.headerStripButton}>
              {headerStrip?.map((head, index) => {
                return (
                  <div style={styles.tabLineContainer} key={index}>
                    {head}
                    {index + 1 !== headerStrip?.length && (
                      <hr color={BORDER.LIGHT} style={styles.horizontalLine} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // Table head when all are not checked.
          <div style={styles.tableHeadContainer}>
            {tableHeads.map((head, index) => {
              return (
                <CustomTooltip
                  title={
                    !tableData?.isLoading &&
                    (window.innerWidth < 1250 || head.label?.length > 10
                      ? head.label
                      : "")
                  }
                >
                  <div
                    style={{
                      ...{
                        ...(index === 0
                          ? {
                              ...styles.initialTableHeadValue,
                              width: head?.width,
                              minWidth: head?.width,
                            }
                          : {
                              ...styles.tableHeadValue,
                              width: head?.width,
                              minWidth: head?.width,
                            }),
                      },
                      ...(index === 0 && tableData?.isSticky
                        ? styles.stickyUserInfoCell
                        : ""),
                    }}
                    key={index}
                  >
                    {index === 0 && !selectionPermission && (
                      <div style={styles.tableHeadCheckbox}>
                        <Checkbox
                          checked={
                            checkedValue?.length === checkedRowSelect?.length &&
                            tableBody?.length === checkedRowSelect?.length
                          }
                          partial={
                            checkedRowSelect?.length &&
                            checkedRowSelect?.length < tableBody?.length
                          }
                          disabled={!tableBody?.length || tableData?.isLoading}
                          onChange={checkedAllHandler}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        ...{
                          ...(index === 0 && !selectionPermission
                            ? {
                                ...styles.userInfoHeaderCellPadding,
                                ...styles.headerLabelSortContainer,
                              }
                            : { ...styles.headerLabelSortContainer }),
                        },
                        ...(selectionPermission && index === 0
                          ? styles.userInfoHeaderCellTotalPadding
                          : ""),
                      }}
                    >
                      <TypographyInline
                        isEllipses
                        label={head.label}
                        color={GREY.PRIMARY}
                        size="sm"
                        fontWeight={fontWeight.MEDIUM}
                      />
                      {head.sort && (
                        <div style={styles.sortImage}>
                          <Image
                            width={fontSize.MD}
                            height={fontSize.MD}
                            src={sortImageHandler(head)}
                            onClick={() => sortHandler(head, index)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </CustomTooltip>
              );
            })}
          </div>
        )}
        {/* Table Head */}
        {/* Table Body */}
        <div style={styles.tableBody}>
          {!tableData?.isLoading && !tableBody?.length ? (
            <div style={styles.emptyRecordContainer}>
              <div>
                <Image
                  className={classes.noRecordFoundImage}
                  src={NO_RECORD_UPDATE}
                />
              </div>
              <TypographyInline
                color={GREY.PRIMARY}
                fontWeight={fontWeight.SEMIBOLD}
                label={STRINGS.NO_RECORD_FOUND}
                size={"xl"}
              />
            </div>
          ) : (
            tableBody?.map((row, index) => {
              return (
                <div style={styles.tableBodyRow} key={index}>
                  {tableHeads.map((head, i) => {
                    return (
                      <>
                        {tableBodyObj[head?.ui_id || "default"](
                          row,
                          head,
                          i,
                          index
                        )}
                      </>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Action Icon Menu  */}
        <CustomMenu
          items={tableOperations}
          anchor={actionMenuAnchor}
          open={isActionMenuVisible}
          onClose={() => {
            setIsActionMenuVisible(false);
            setActionMenuAnchor(null);
          }}
          rowData={clickedRowData}
          headData={clickedHeadData}
        />
        {/* Action Icon Menu  */}

        {/* Lead Status Menu */}
        <CustomMenu
          items={tableStausList}
          anchor={leadStatusMenuAnchor}
          open={Boolean(leadStatusMenuAnchor)}
          onClose={() => {
            setLeadStatusMenuAnchor(null);
          }}
          rowData={clickedRowData}
          headData={clickedHeadData}
          isLeadStatus={true}
        />
        {/* Lead Status Menu */}

        {/* Table Body */}
      </div>
      {/* Pagination */}
      <CustomPagination total_records={tableData.total_records} id={table_id} />
    </div>
  );
};

export default React.memo(CustomTable);
